import React, { useEffect, useState } from "react";
import { ArrowDownRight, ArrowUpRight, Download, Minus } from "lucide-react";
import { toast } from "sonner";
import { api, downloadCsv, rp } from "@/lib/api";

const PERIODS = [
  ["today", "Hari ini"], ["last7", "7 hari"], ["month", "Bulan ini"],
  ["prev_month", "Bulan lalu"], ["last30", "30 hari"], ["last90", "90 hari"],
];

const Delta = ({ value }) => {
  const up = value > 0;
  const flat = !value;
  const Icon = flat ? Minus : up ? ArrowUpRight : ArrowDownRight;
  const cls = flat ? "text-ink-soft" : up ? "text-emerald-600" : "text-lm-red";
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-semibold ${cls}`}>
      <Icon size={12} />{Math.abs(value)}%
    </span>
  );
};

const Card = ({ label, value, sub, delta, testid }) => (
  <div className="bg-white border border-black/10 rounded-lg p-4" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-widest text-ink-soft">{label}</div>
    <div className="mt-1.5 text-xl md:text-2xl font-semibold tabular-nums">{value}</div>
    <div className="mt-1 flex items-center gap-2">
      {delta !== undefined && <Delta value={delta} />}
      {sub && <span className="text-[11px] text-ink-soft truncate">{sub}</span>}
    </div>
  </div>
);

const BarRow = ({ label, value, max, right, testid }) => (
  <div className="py-2" data-testid={testid}>
    <div className="flex justify-between gap-3 text-xs">
      <span className="truncate">{label}</span>
      <span className="text-ink-soft whitespace-nowrap">{right}</span>
    </div>
    <div className="mt-1 h-1.5 rounded-full bg-black/5 overflow-hidden">
      <div className="h-full rounded-full bg-lm-red/80 transition-all duration-500"
        style={{ width: `${max ? Math.max(3, (value / max) * 100) : 0}%` }} />
    </div>
  </div>
);

const DailyChart = ({ daily }) => {
  const max = Math.max(1, ...daily.map((d) => d.revenue));
  const slice = daily.slice(-45);
  return (
    <div className="bg-white border border-black/10 rounded-lg p-4" data-testid="analytics-daily-chart">
      <div className="flex items-baseline justify-between">
        <h3 className="text-sm font-semibold">Revenue harian</h3>
        <span className="text-[11px] text-ink-soft">{slice.length} hari terakhir dalam periode</span>
      </div>
      <div className="mt-4 flex items-end gap-[3px] h-28">
        {slice.map((d) => (
          <div key={d.date} title={`${d.date} · ${rp(d.revenue)} · ${d.bookings} booking`}
            className="flex-1 min-w-[3px] rounded-t bg-ink/80 hover:bg-lm-red transition-colors"
            style={{ height: `${Math.max(2, (d.revenue / max) * 100)}%` }} />
        ))}
      </div>
      <div className="mt-2 flex justify-between text-[10px] text-ink-soft">
        <span>{slice[0]?.date}</span><span>{slice[slice.length - 1]?.date}</span>
      </div>
    </div>
  );
};

export default function AnalyticsPanel({ branchId }) {
  const [period, setPeriod] = useState("month");
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    setErr("");
    api.get("/analytics/overview", { params: { period, branch_id: branchId || undefined } })
      .then((r) => setD(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Analitik tidak tersedia untuk role Anda"));
  }, [period, branchId]);

  if (err) return <div className="text-sm text-ink-soft" data-testid="analytics-error">{err}</div>;
  if (!d) return <div className="text-sm text-ink-soft">Memuat analitik…</div>;

  const maxTier = Math.max(1, ...d.membership.tiers.map((t) => t.customers));
  const maxBranch = Math.max(1, ...d.branches.map((b) => b.revenue));
  const maxPkg = Math.max(1, ...d.packages.map((p) => p.revenue));

  return (
    <section className="space-y-4" data-testid="analytics-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-base md:text-lg font-semibold">Analitik · {d.period.label}</h2>
        <div className="flex gap-1.5 flex-wrap items-center">
          {PERIODS.map(([k, label]) => (
            <button key={k} data-testid={`period-${k}`} onClick={() => setPeriod(k)}
              className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${period === k
                ? "bg-ink text-cream border-ink" : "border-black/15 hover:border-ink/40"}`}>{label}</button>
          ))}
          <button data-testid="analytics-export-csv"
            onClick={() => downloadCsv("/analytics/export.csv", { period, branch_id: branchId || undefined },
              `analitik-${period}-${d.period.date_to}.csv`)
              .then(() => toast.success("CSV analitik diunduh"))
              .catch(() => toast.error("Gagal mengunduh CSV"))}
            className="px-3 py-1.5 rounded-full text-xs border border-black/15 hover:border-ink/40 flex items-center gap-1.5">
            <Download size={12} /> CSV
          </button>
        </div>
      </div>
      <p className="text-[11px] text-ink-soft -mt-2">
        {d.period.date_from} → {d.period.date_to} · dibandingkan {d.previous.date_from} → {d.previous.date_to}
      </p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card label="Revenue" value={rp(d.current.revenue)} delta={d.delta.revenue}
          sub={`sebelumnya ${rp(d.prev.revenue)}`} testid="analytics-revenue" />
        <Card label="Revenue Lunas" value={rp(d.current.paid_revenue)} delta={d.delta.paid_revenue}
          sub={`${d.current.paid_bookings} booking lunas`} testid="analytics-paid-revenue" />
        <Card label="Booking" value={d.current.bookings} delta={d.delta.bookings}
          sub={`${d.current.cancelled} dibatalkan`} testid="analytics-bookings" />
        <Card label="Rata-rata / Booking" value={rp(d.current.avg_order_value)}
          sub={`${d.current.units_rented} unit tersewa`} testid="analytics-aov" />
        <Card label="Sedang Disewa" value={d.active_rentals} sub="status jalan sekarang" testid="analytics-active" />
        <Card label="Deposit Ditahan" value={rp(d.deposits_held)} sub={`${d.deposits_held_count} booking`}
          testid="analytics-deposits" />
        <Card label="Total Diskon" value={rp(d.current.discounts)} sub="promo + member + reward"
          testid="analytics-discounts" />
        <Card label="Akun Member" value={`${d.membership.members_linked}/${d.membership.total_customers}`}
          sub={`${d.membership.rewards_available} reward siap pakai`} testid="analytics-members" />
      </div>

      <DailyChart daily={d.daily} />

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white border border-black/10 rounded-lg p-4" data-testid="analytics-tiers">
          <h3 className="text-sm font-semibold">Sebaran Tier Member</h3>
          <div className="mt-2 divide-y divide-black/5">
            {d.membership.tiers.map((t) => (
              <BarRow key={t.name} testid={`tier-row-${t.name}`} label={`${t.name} · diskon ${t.discount_percent}%`}
                value={t.customers} max={maxTier} right={`${t.customers} customer · ${t.share}%`} />
            ))}
          </div>
        </div>

        <div className="bg-white border border-black/10 rounded-lg p-4" data-testid="analytics-branches">
          <h3 className="text-sm font-semibold">Performa per Cabang</h3>
          <div className="mt-2 divide-y divide-black/5">
            {d.branches.length === 0 && <div className="py-3 text-xs text-ink-soft">Belum ada booking pada periode ini.</div>}
            {d.branches.map((b) => (
              <BarRow key={b.key || b.label} testid={`branch-row-${b.code || b.label}`} label={b.label}
                value={b.revenue} max={maxBranch} right={`${rp(b.revenue)} · ${b.bookings} booking`} />
            ))}
          </div>
        </div>

        <div className="bg-white border border-black/10 rounded-lg p-4" data-testid="analytics-packages">
          <h3 className="text-sm font-semibold">Paket Terlaris</h3>
          <div className="mt-2 divide-y divide-black/5">
            {d.packages.length === 0 && <div className="py-3 text-xs text-ink-soft">Belum ada data.</div>}
            {d.packages.map((p) => (
              <BarRow key={p.label} label={p.label} value={p.revenue} max={maxPkg}
                right={`${rp(p.revenue)} · ${p.bookings}x`} />
            ))}
          </div>
        </div>

        <div className="bg-white border border-black/10 rounded-lg p-4" data-testid="analytics-top-units">
          <h3 className="text-sm font-semibold">Unit Paling Produktif</h3>
          <div className="mt-2 divide-y divide-black/5 text-xs">
            {d.top_units.length === 0 && <div className="py-3 text-ink-soft">Belum ada unit yang di-assign pada periode ini.</div>}
            {d.top_units.map((u) => (
              <div key={u.unit_id} className="flex items-center justify-between gap-3 py-2" data-testid={`top-unit-${u.asset_id}`}>
                <div className="min-w-0">
                  <div className="font-medium truncate">{u.asset_id} · {u.name || "—"}</div>
                  <div className="text-ink-soft">{u.status} · total seumur hidup {u.lifetime_rentals}x</div>
                </div>
                <div className="text-right whitespace-nowrap">
                  <div className="font-semibold">{u.rentals}x</div>
                  <div className="text-ink-soft">{rp(u.revenue)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
